import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { ediCatalogItemAttachmentDownloadUrl, useEdiCatalogItemAttachmentList } from '@/api/hanly-v2/edi/edi';
import type { EdiCatalogItemAttachment } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { formatYMDSlash } from '@/utils/date';
import { downloadBlob, getFileExtensionLabel } from '@/utils/file';

interface ProductAttachmentsProps {
  /** カタログアイテムのID */
  itemId: number;
}

const downloadAttachment = async (itemId: number, attachment: EdiCatalogItemAttachment) => {
  const { presigned_url } = await ediCatalogItemAttachmentDownloadUrl(itemId, attachment.id);
  const response = await fetch(presigned_url);
  if (!response.ok) {
    throw new Error('ファイルの取得に失敗しました');
  }
  downloadBlob(await response.blob(), attachment.name);
};

/**
 * 商品の添付ファイル一覧。非公開バケットのファイルなのでダウンロードのたびに URL を発行する。
 * 添付がない商品では見出しごと出さない。
 */
export const ProductAttachments = ({ itemId }: ProductAttachmentsProps) => {
  const { data, isLoading, isError } = useEdiCatalogItemAttachmentList(itemId);
  const [downloadingIds, setDownloadingIds] = useState<ReadonlySet<number>>(new Set());

  const handleDownload = async (attachment: EdiCatalogItemAttachment) => {
    setDownloadingIds((prev) => new Set(prev).add(attachment.id));
    try {
      await downloadAttachment(itemId, attachment);
    } catch {
      toast.error('添付ファイルのダウンロードに失敗しました');
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(attachment.id);
        return next;
      });
    }
  };

  if (isLoading) {
    return null;
  }
  if (isError) {
    return <p className="text-sm text-neutral-600">添付ファイルの取得に失敗しました</p>;
  }
  const attachments = data ?? [];
  if (attachments.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-1.5">
      <h4 className="text-sm font-bold">添付ファイル（{attachments.length}件）</h4>
      <ul className="divide-y divide-neutral-100 border border-neutral-200">
        {attachments.map((attachment) => {
          const isDownloading = downloadingIds.has(attachment.id);
          const extension = getFileExtensionLabel(attachment.name);
          return (
            <li key={attachment.id}>
              <button
                type="button"
                onClick={() => handleDownload(attachment)}
                disabled={isDownloading}
                className="flex min-h-14 w-full cursor-pointer items-center gap-3 px-3 py-2 text-left hover:bg-neutral-50 disabled:cursor-wait disabled:opacity-60"
              >
                <span
                  className={clsx(
                    'flex h-10 w-9 shrink-0 items-center justify-center text-[10px] font-bold',
                    extension === 'PDF' ? 'bg-red-100 text-red-700' : 'bg-neutral-100 text-neutral-700',
                  )}
                >
                  {extension}
                </span>
                <span className="flex min-w-0 flex-1 flex-col leading-snug">
                  <span className="truncate text-sm" title={attachment.name}>
                    {attachment.name}
                  </span>
                  <span className="text-xs text-neutral-600">
                    登録日 {formatYMDSlash(attachment.created_at)}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1 text-sm text-sky-800">
                  <ArrowDownTrayIcon className="size-5" aria-hidden="true" />
                  <span className="hidden sm:inline">
                    {isDownloading ? 'ダウンロード中...' : 'ダウンロード'}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
